import React, { useCallback, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Popover } from 'devextreme-react/popover';
import { Tooltip } from 'devextreme-react/tooltip';
import { HoverTip, TooltipEvent, groupTipEvents } from './eventTooltipTypes';
import { eventIcon } from './eventIcon';
import { buildDeepLinkHash } from '../tasklist/deepLinkUtils';

// The interactive hover tooltip shared by the Progress Monitor and Dashboard timelines. It lists a
// marker's files/comments/notes and lets file events download on click (vis's native title tooltip
// can't hold clickable links, so we render our own). The panel is hover-into so those links stay
// clickable; the owning controller drives visibility via the HoverTip state + enter/leave callbacks.

interface EventTooltipProps {
    hoverTip: HoverTip | null;
    onTipEnter: () => void;
    onTipLeave: () => void;
    downloadEventFile: (fileId: string, fileName: string) => void;
    // Opens the event's task (includeSubtask false) or subtask (true). The caller navigates, since
    // that differs by page. Omitted = no open links.
    openEventLocation?: (ev: TooltipEvent, includeSubtask: boolean) => void;
}

// How much of a comment to show before the "…more" toggle. Generous — long comments wrap over a
// few lines so most are fully readable without expanding; only the really long ones get clipped.
const COMMENT_SNIPPET_LEN = 140;

// A comment line: shows the author and comment text, collapsing very long comments to a snippet with
// an inline "…more" that reveals the full text in a DevExtreme tooltip on hover (no click — expanding
// inline would resize the events tooltip and shift everything around).
const TipComment: React.FC<{ userName: string; text: string }> = ({ userName, text }) => {
    const isLong = text.length > COMMENT_SNIPPET_LEN;
    const shown = isLong ? text.slice(0, COMMENT_SNIPPET_LEN).trimEnd() : text;
    // useId yields ids containing ':' which aren't valid in a CSS selector; sanitize for the target.
    const moreId = `pt-more-${useId().replace(/:/g, '')}`;
    return (
        <span className="pt-tip-comment">
            {userName ? <em className="pt-tip-who">{userName}:&nbsp;</em> : null}
            {shown}
            {isLong && (
                <>
                    {' '}
                    <span id={moreId} className="pt-tip-more">…more</span>
                    <Tooltip
                        target={`#${moreId}`}
                        showEvent="mouseenter"
                        hideEvent="mouseleave"
                        position="top"
                        wrapperAttr={{ class: 'pt-more-tip' }}
                    >
                        <div className="pt-more-tip-content">{text}</div>
                    </Tooltip>
                </>
            )}
        </span>
    );
};

// The scrollable day/event content of the tooltip (the header is rendered separately so it stays
// fixed above the scroll area).
const renderTipBody = (
    tip: HoverTip,
    downloadEventFile: (fileId: string, fileName: string) => void,
    openEventLocation?: (ev: TooltipEvent, includeSubtask: boolean) => void,
) => {
    // Tag files "Original Submission" / "Resubmission N" only when their phase has resubmissions. That
    // is decided per phase across the whole grant (ev.phaseHasResubmissions), not from what this
    // tooltip happens to show — so an original-submission file still gets tagged when its phase has
    // resubmissions on other dates.
    const submissionTag = (ev: TooltipEvent): string | null => {
        if (ev.type !== 'File' || !ev.phaseHasResubmissions) return null;
        return ev.resubmission == null ? 'Original Submission' : `Resubmission ${ev.resubmission}`;
    };
    return (
    <>
        {tip.days.map((day, di) => (
            <React.Fragment key={di}>
                <div className="pt-tip-date">Events on {day.dateStr}</div>
                {groupTipEvents(day.events, tip.depth).map((sec, si, secs) => {
                    // Only print a heading level when it differs from the section just above — so
                    // sibling steps of one phase don't reprint the phase name. Once a level differs,
                    // every deeper level prints too (it belongs under the new parent).
                    const prev = secs[si - 1]?.path ?? [];
                    let diverged = false;
                    return (
                    <div className="pt-tip-section" key={si}>
                        {sec.path.map((heading, hi) => {
                            const show = diverged || prev[hi] !== heading;
                            if (show) diverged = true;
                            if (!show) return null;

                            // At 'modules' depth the path is [task, subtask]; at 'phases' it is just
                            // [subtask]. Every event in a section shares the section's location, so
                            // the first one carries the ids. A task-level event has no stepId, so its
                            // subtask heading resolves to the task link anyway.
                            const ev = sec.events[0];
                            const isTaskHeading = tip.depth === 'modules' && hi === 0;
                            const stepId = isTaskHeading ? null : ev.stepId;
                            const canOpen = !!openEventLocation
                                && !!buildDeepLinkHash(ev.moduleTypeId, ev.phaseId, stepId);

                            return (
                                <div className="pt-tip-grp" key={hi} style={{ paddingLeft: hi * 12 }}>
                                    {heading}
                                    {canOpen && (
                                        <a
                                            className="pt-tip-open"
                                            title={isTaskHeading ? 'Open this task' : 'Open this subtask'}
                                            onClick={() => openEventLocation?.(ev, !isTaskHeading)}
                                        >
                                            <i className="dx-icon-link" />
                                        </a>
                                    )}
                                </div>
                            );
                        })}
                        {sec.events.map((ev, i) => {
                            const icon = eventIcon(ev.type, ev.commentType);
                            const isFile = ev.type === 'File' && !!ev.fileId;
                            const tag = submissionTag(ev);
                            return (
                                <div className="pt-tip-line" key={i} style={{ paddingLeft: sec.path.length * 12 }}>
                                    <i className={icon} />
                                    {ev.type === 'Comment' ? (
                                        <TipComment userName={ev.userName} text={ev.label} />
                                    ) : (
                                        <>
                                            {isFile ? (
                                                <a
                                                    className="pt-tip-file"
                                                    title="Download"
                                                    onClick={() => downloadEventFile(ev.fileId as string, ev.fileName ?? ev.label)}
                                                >
                                                    {ev.label}
                                                </a>
                                            ) : (
                                                <span>{ev.label}</span>
                                            )}
                                            {tag && <span className="pt-tip-resub">{tag}</span>}
                                            {ev.userName
                                                ? <em className="pt-tip-who"> {ev.type === 'File' ? 'uploaded ' : ''}by {ev.userName}</em>
                                                : ev.type === 'File'
                                                    ? <em className="pt-tip-who"> uploaded</em>
                                                    : null}
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    );
                })}
            </React.Fragment>
        ))}
        {tip.extraDays > 0 && (
            <div className="pt-tip-date">…and {tip.extraDays} more day(s)</div>
        )}
    </>
    );
};

const TIP_STYLE = `
    .pt-tip-date {
        font-weight: 700;
        margin-top: 6px;
        padding-top: 4px;
        border-top: 1px solid rgba(0, 0, 0, 0.15);
    }
    .pt-tip-date:first-child { margin-top: 0; padding-top: 0; border-top: 0; }
    /* Event tooltip: let DX size the popover (capped at maxHeight) and have the content fill it. The
       flex chain (popup-content → pt-dx-tip → pt-tip-scroll) lets the header stay fixed while the
       day/event list fills the remaining height and scrolls when it overflows. min-height:0 on each
       level is what lets the scroll child shrink inside a flex parent. */
    .pt-tip-popover .dx-popup-content {
        display: flex;
        flex-direction: column;
        min-height: 0;
    }
    /* No font-size/family here: inherit the DevExtreme overlay defaults so this matches the legacy
       timeline's DX <Tooltip> content exactly. */
    .pt-dx-tip {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
    }
    /* Day/event list fills the leftover height; scrollbar only when it overflows. */
    .pt-tip-scroll {
        flex: 1 1 auto;
        min-height: 0;
        overflow-y: auto;
        margin-top: 4px;
    }
    .pt-tip-body .pt-tip-header { font-weight: 700; }
    .pt-tip-body .pt-tip-section { margin-top: 2px; }
    .pt-tip-body .pt-tip-grp { font-weight: 600; color: #555; margin-top: 5px; }
    /* Link icon beside a task/subtask heading - deep links to it on the grant page. */
    .pt-tip-body .pt-tip-open {
        margin-left: 6px;
        color: #1565C0;
        cursor: pointer;
        vertical-align: middle;
        text-decoration: none;
    }
    .pt-tip-body .pt-tip-open i {
        font-size: 14px;
        /* The heading's own colour rule would otherwise grey this out. */
        color: inherit;
    }
    /* Darken on hover rather than underline - an underlined icon reads as a stray mark. */
    .pt-tip-body .pt-tip-open:hover { color: #0D47A1; text-decoration: none; }
    .pt-tip-body .pt-tip-line { margin-top: 3px; }
    .pt-tip-body .pt-tip-line i { margin-right: 4px; color: #777; }
    .pt-tip-body .pt-tip-who { color: #888; }
    /* Comment line: author and text read as "username: comment" and wrap naturally over a few lines.
       Long comments are clipped to a snippet with an inline "…more" toggle. */
    .pt-tip-body .pt-tip-comment { word-break: break-word; }
    .pt-tip-body .pt-tip-more {
        color: #1565C0;
        cursor: default;
        white-space: nowrap;
    }
    .pt-tip-body .pt-tip-more:hover { text-decoration: underline; }
    /* Full-comment DevExtreme tooltip shown when hovering "…more": left-justified and up to twice the
       width of the events tooltip. */
    .pt-more-tip .dx-popup-content {
        max-width: 760px;
        text-align: left;
        white-space: normal;
        word-break: break-word;
    }
    .pt-tip-body .pt-tip-file {
        color: #1565C0;
        cursor: pointer;
        text-decoration: none;
    }
    .pt-tip-body .pt-tip-file:hover { text-decoration: underline; }
    /* Submission tag on a file line, shown only when the phase has resubmissions: a small muted pill
       marking each file as "Original Submission" or "Resubmission N". */
    .pt-tip-body .pt-tip-resub {
        display: inline-block;
        margin-left: 6px;
        padding: 0 6px;
        border-radius: 8px;
        font-size: 10px;
        font-weight: 600;
        line-height: 15px;
        color: #555;
        background: rgba(0, 0, 0, 0.07);
        white-space: nowrap;
        vertical-align: baseline;
    }
`;

const EventTooltip: React.FC<EventTooltipProps> = ({ hoverTip, onTipEnter, onTipLeave, downloadEventFile, openEventLocation }) => {
    // Mouse-wheel scrolling for the tooltip's scroll region. React attaches `wheel` as a passive
    // listener (so preventDefault is ignored) and vis/Hammer can also swallow it, so the element's
    // native overflow scroll doesn't always fire. We attach our own non-passive listener via a ref
    // callback (fires whenever DX mounts/unmounts the content) and scroll the element ourselves.
    const tipWheelCleanup = useRef<(() => void) | null>(null);
    const attachTipScroll = useCallback((el: HTMLDivElement | null) => {
        if (tipWheelCleanup.current) { tipWheelCleanup.current(); tipWheelCleanup.current = null; }
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            if (el.scrollHeight <= el.clientHeight) return; // nothing to scroll → let it pass through
            e.preventDefault();
            e.stopPropagation();
            el.scrollTop += e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        tipWheelCleanup.current = () => el.removeEventListener('wheel', onWheel);
    }, []);

    if (!hoverTip) return null;

    return (
        <>
            <style>{TIP_STYLE}</style>
            {/* An invisible anchor placed at the hovered marker's box, portalled to <body> so it
                escapes the timeline's clipping. Keyed by position so DX re-evaluates placement when
                you move to a new marker. */}
            {createPortal(
                <div
                    id="pt-dx-anchor"
                    style={{
                        position: 'fixed',
                        left: hoverTip.anchorLeft,
                        top: hoverTip.anchorTop,
                        width: Math.max(1, hoverTip.anchorRight - hoverTip.anchorLeft),
                        height: Math.max(1, hoverTip.anchorBottom - hoverTip.anchorTop),
                        pointerEvents: 'none',
                    }}
                />,
                document.body
            )}
            <Popover
                key={`${hoverTip.anchorLeft}-${hoverTip.anchorTop}`}
                target="#pt-dx-anchor"
                position="bottom"
                visible={true}
                width={380}
                // Let DX cap the popover height to ~60% of the viewport. It still sizes to content
                // when the tooltip is short; when taller, the inner content flex-fills and scrolls.
                maxHeight={() => Math.round(window.innerHeight * 0.6)}
                wrapperAttr={{ class: 'pt-tip-popover' }}
                shading={false}
                hideOnOutsideClick={false}
                contentRender={() => (
                    <div
                        className="pt-dx-tip pt-tip-body"
                        onMouseEnter={onTipEnter}
                        onMouseLeave={onTipLeave}
                    >
                        {hoverTip.header && <div className="pt-tip-header">{hoverTip.header}</div>}
                        <div className="pt-tip-scroll" ref={attachTipScroll}>
                            {renderTipBody(hoverTip, downloadEventFile, openEventLocation)}
                        </div>
                    </div>
                )}
            />
        </>
    );
};

export default EventTooltip;
